import { createSignal, For, Match, Show, Switch } from "solid-js";

import socket from "./socket";
import ResultsTable from "./ResultsTable";
import AdminUsers from "./AdminUsers";
import { useAdminContext } from "./AdminContext";
import ProblemStartingDate from "./ProblemStartingDate";
import { formatInParis } from "./dateTime";

export default function Admin() {

    const [adminStore, setAdminStore] = useAdminContext() as any;
        

    const [activeTab, setActiveTab] = createSignal('Liste Etudiants');
    
    const tabClass = (tabName: string) => {
        return activeTab() === tabName ? 'nav-link active' : 'nav-link';
    }
    const handleTabClick = (tabName: string) => (event: Event) => {
        setActiveTab(tabName);
        event.preventDefault();
    };
    return <>
        <ul class="nav nav-tabs">
            <li class="nav-item">
                <a 
                    class={tabClass('Liste Etudiants')}
                    aria-current="page" href="#"
                    onClick={handleTabClick('Liste Etudiants')}>Liste Etudiants</a>
            </li>
            <li class="nav-item">
                <a class={tabClass('Tableaux des résultats')} href="#"
                    onClick={handleTabClick('Tableaux des résultats')}>Tableaux des résultats</a>
            </li>
            <li class="nav-item">
                <a class={tabClass('Liste des Utilisateurs')} href="#"
                    onClick={handleTabClick('Liste des Utilisateurs')}>Liste des Utilisateurs</a>
            </li>
            <li class="nav-item">
                <a class={tabClass('Problèmes')} href="#"
                    onClick={handleTabClick('Problèmes')}>Problèmes</a>
            </li>
        </ul>
        <Switch>
            <Show when={adminStore.users}>
                <Match when={activeTab() === 'Liste Etudiants'}>
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Username</th>
                        <th>Repo</th>
                        <th>Last pipeline</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                <For each={adminStore.users} fallback={<div>Loading...</div>}>
                    {(item, index) => (
                        <tr>
                            <td>{index()+1}</td>
                            <td>
        {item.username} {item.role === 'admin' ? '👑' : ''}
                            </td>
                                                        <td>
        {item.project_id ? <a target="_blank" href={item.project_url}>{item.project_url}</a> : 'No project'}
                            </td>
                            <td>
                                <Show when={adminStore.pipelines && adminStore.pipelines[item.id]} fallback={'—'}>
                                    {(() => {
                                        const p = adminStore.pipelines[item.id];
                                        const status = p?.status || 'unknown';
                                        const color = status === 'success' ? 'green' : status === 'failed' ? 'red' : status === 'running' ? 'orange' : 'gray';
                                        return <span style={{ color }}>{status}</span>;
                                    })()}
                                </Show>
                            </td>
                            <td>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary"
                                    disabled={!item.project_id}
                                    onClick={() => {
                                        const ref = 'master';
                                        socket.emit('trigger-pipeline', { user_id: item.id, ref });
                                    }}
                                >Trigger pipeline</button>
                            </td>
                        </tr>
                    )}
                </For>
                </tbody>
            </table>
            </Match>
        </Show>
        <Match when={activeTab() === 'Tableaux des résultats'}>
            <div>
                <button 
                    type="button" 
                    class="btn btn btn-primary btn-sm" 
                    disabled={adminStore.resultsFetching} 
                    onClick={() => {
                        socket.emit('fetch-results');
                        setAdminStore('resultsFetching', true);
                    }}>Refresh Results</button>
                <small class="text-muted ms-2">Results are automatically updated when pipelines complete</small>
            </div>
            <Show when={adminStore.results && adminStore.users}>
                <ResultsTable />
            </Show>
            <Show when={adminStore.resultsFetching}>
                <div>Refreshing results...</div>
            </Show>
            <Show when={!adminStore.results && !adminStore.resultsFetching}>
                <div>No results available yet. Results will appear automatically when students complete their assignments.</div>
            </Show>
        </Match>
        <Match when={activeTab() === 'Liste des Utilisateurs'}>
            <AdminUsers />
        </Match>
        <Match when={activeTab() === 'Problèmes'}>
            <div class="mb-3 d-flex align-items-center gap-2">
                <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    disabled={adminStore.problemsImporting}
                    onClick={() => {
                        setAdminStore({ problemsImporting: true, problemsImportMessage: null });
                        socket.emit('import-latest-problems');
                    }}
                >{adminStore.problemsImporting ? 'Import en cours...' : 'Importer le dernier pipeline'}</button>
                <Show when={adminStore.problemsImportMessage}>
                    <span class="small">{adminStore.problemsImportMessage}</span>
                </Show>
            </div>
            <table class="table table-sm">
                <thead>
                    <tr>
                        <th>Problème</th>
                        <th>Date de début</th>
                        <th>Dernière mise à jour</th>
                        <th>Alertes</th>
                    </tr>
                </thead>
                <tbody>
                    <For each={adminStore.problems} fallback={<tr><td colSpan="4">Aucun problème importé.</td></tr>}>
                        {(problem, index) => (
                            <tr>
                                <td>{problem.problem_key}</td>
                                <td>
                                    <ProblemStartingDate
                                        problemKey={problem.problem_key}
                                        defaultValue={problem.default_starting_date}
                                        overrideValue={problem.starting_date_override}
                                        hasOverride={problem.has_starting_date_override}
                                        previousValue={index() > 0
                                            ? adminStore.problems[index() - 1].effective_starting_date
                                            : null}
                                    />
                                </td>
                                <td>{formatInParis(problem.last_updated.replace(' ', 'T') + 'Z')}</td>
                                <td>
                                    <Show when={problem.missing_from_latest_pipeline}><div class="text-warning">Absent du dernier pipeline</div></Show>
                                    <Show when={problem.starting_date_too_old}><div class="text-danger">Date de début invalide ou vieille de plus de 6 mois</div></Show>
                                    <Show when={problem.has_starting_date_override}><div class="text-info">Date remplacée par un administrateur</div></Show>
                                </td>
                            </tr>
                        )}
                    </For>
                </tbody>
            </table>
        </Match>
        </Switch>
    </>
}
